/**
 * First-party, on-device list of recently viewed product handles, shown in the
 * account drawer (e.g. the signed-out "Recently Viewed" row). Persisted in
 * localStorage so the list survives the tab, and never sent anywhere by
 * itself — consumers fetch public product-card data for the stored handles via
 * `/api/products/cards`.
 *
 * Handles are validated against the same shape `parseStorefrontHandle`
 * enforces server-side, so a corrupted or tampered store can never produce a
 * request the public API would reject.
 */

const STORAGE_KEY = 'wm_recently_viewed';
const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const MAX_HANDLE_LENGTH = 255;

export const RECENTLY_VIEWED_MAX_ENTRIES = 12;
export const RECENTLY_VIEWED_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export interface RecentlyViewedEntry {
	handle: string;
	/** Epoch millis of the most recent view. */
	ts: number;
}

export function isValidRecentlyViewedHandle(handle: unknown): handle is string {
	return (
		typeof handle === 'string' &&
		handle.length > 0 &&
		handle.length <= MAX_HANDLE_LENGTH &&
		HANDLE_PATTERN.test(handle)
	);
}

function toRecentlyViewedEntry(item: unknown, now: number): RecentlyViewedEntry | null {
	if (typeof item !== 'object' || item === null) return null;
	const { handle, ts } = item as { handle?: unknown; ts?: unknown };
	if (!isValidRecentlyViewedHandle(handle)) return null;
	if (typeof ts !== 'number' || !Number.isFinite(ts)) return null;
	if (now - ts > RECENTLY_VIEWED_MAX_AGE_MS || ts > now + 60_000) return null;
	return { handle, ts };
}

/**
 * Parse a raw storage payload into a clean entry list: drops malformed
 * entries, stale entries, and duplicate handles (keeping the most recent),
 * and caps the list length. Never throws.
 */
export function parseStoredRecentlyViewed(raw: string | null, now: number): RecentlyViewedEntry[] {
	if (!raw) return [];

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return [];
	}
	if (!Array.isArray(parsed)) return [];

	const entries: RecentlyViewedEntry[] = [];
	const seen = new Set<string>();

	for (const item of parsed) {
		const entry = toRecentlyViewedEntry(item, now);
		if (!entry || seen.has(entry.handle)) continue;
		seen.add(entry.handle);
		entries.push(entry);
		if (entries.length >= RECENTLY_VIEWED_MAX_ENTRIES) break;
	}

	return entries;
}

/**
 * Move-or-insert `handle` at the front of `entries` with timestamp `now`,
 * dropping any older entry for the same handle and capping the list.
 */
export function computeNextRecentlyViewed(
	entries: RecentlyViewedEntry[],
	handle: string,
	now: number,
): RecentlyViewedEntry[] {
	if (!isValidRecentlyViewedHandle(handle)) return entries;

	const next = [{ handle, ts: now }];
	for (const entry of entries) {
		if (entry.handle === handle) continue;
		next.push(entry);
		if (next.length >= RECENTLY_VIEWED_MAX_ENTRIES) break;
	}
	return next;
}

function readStoredEntries(now: number): RecentlyViewedEntry[] {
	try {
		return parseStoredRecentlyViewed(window.localStorage.getItem(STORAGE_KEY), now);
	} catch {
		// Storage unavailable (private mode, blocked, SSR-adjacent edge).
		return [];
	}
}

/** Record a product view. Safe to call anywhere; no-ops without a browser. */
export function recordRecentlyViewedProduct(handle: string) {
	if (typeof window === 'undefined') return;

	const now = Date.now();
	const next = computeNextRecentlyViewed(readStoredEntries(now), handle, now);

	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
	} catch {
		// Storage unavailable — viewing history is a nice-to-have, drop it.
	}
}

/** Most-recent-first product handles, freshest views first. */
export function getRecentlyViewedHandles(limit = RECENTLY_VIEWED_MAX_ENTRIES): string[] {
	if (typeof window === 'undefined') return [];
	return readStoredEntries(Date.now())
		.slice(0, Math.max(0, limit))
		.map((entry) => entry.handle);
}
